import { useLocalizationContext } from '@va/localization';
import { CircularProgress, fontWeights, Paragraph, ParagraphWithTooltip } from '@va/ui/design-system';
import { addNumberSeparator } from '@va/util/helpers';
import classNames from 'classnames';
import { forwardRef, useMemo } from 'react';

type WebsiteConsumptionRowProps = {
  domain: string;
  limit: number;
  consumption: number;
};

export const WebsiteConsumptionRow = forwardRef<HTMLDivElement, WebsiteConsumptionRowProps>(
  ({ domain, limit, consumption }, ref) => {
    const { locale, translate } = useLocalizationContext();

    const consumptionPercent = useMemo(() => (consumption * 100) / limit, [consumption, limit]);
    const hasInfiniteCredits = useMemo(() => limit === -1, [limit]);

    return (
      <div className='flex items-center gap-3' ref={ref}>
        <ParagraphWithTooltip weight={fontWeights.medium}>{domain}</ParagraphWithTooltip>
        {hasInfiniteCredits && (
          <Paragraph
            className='ml-auto'
            weight={fontWeights.medium}
            colorClassName={classNames({ 'text-white': !hasInfiniteCredits, 'text-orange-dark': hasInfiniteCredits })}
          >
            {translate('feature.counter.counting', {
              behaviourCount: addNumberSeparator(consumption, locale),
              featureLimit: '∞',
            })}
          </Paragraph>
        )}
        {!hasInfiniteCredits && (
          <>
            <Paragraph className='ml-auto' weight={fontWeights.medium} colorClassName='text-white'>
              {addNumberSeparator(consumption, locale)}
            </Paragraph>
            <Paragraph weight={fontWeights.medium} colorClassName='text-white'>
              {addNumberSeparator(consumptionPercent, locale, {
                style: 'percent',
                maximumFractionDigits: 1,
              })}
            </Paragraph>
            <CircularProgress progress={consumptionPercent} gradientColor='#ff8000' radius={21} />
          </>
        )}
      </div>
    );
  },
);
